/// <amd-module name="Release/EnvironmentNode/EnvironmentNode" />
import * as React from "react";

import { css } from "VSSUI/Util";

import { VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { format, localeFormat } from "VSS/Core/Util/String";
import { ago } from "VSSUI/Utilities/Date";
import { Tooltip } from "VSSUI/TooltipEx";
import { Status, IStatusProps, StatusSize } from "VSSUI/Status";

import { ReleaseApproval, ApprovalType, ManualIntervention, ManualInterventionStatus } from "Release/Client/Contracts/Release";
import { handleEventAndNavigate, createUrlForRoute } from "Release/Helpers/Link";
import { ComputedDeploymentStatus, IActiveReleaseEnvironmentNodeProps } from "./Types";
import { getStatusText, getComputedStatusForEnvironment, getBorderColorCssPrefix, getIconStatusProps } from "Helpers";
import { getFirstMIWithStatus, ReleaseEnvironmentStatusIndicator } from "StatusComputer";
import * as Resources from "Release/EnvironmentNode/Resources";

import "VSS/LoaderPlugins/Css!RM:Release/EnvironmentNode/EnvironmentNode";

const environmentNodeHeight: number = 24;

export class EnvironmentNode extends VssComponent<IActiveReleaseEnvironmentNodeProps, {}> {

    public render(): JSX.Element {

        let releaseEnvironment = this.props.environment;
        let statusIndicator = getBorderColorCssPrefix(releaseEnvironment);
        let statusText = getStatusText(releaseEnvironment);
        let computedStatus = getComputedStatusForEnvironment(releaseEnvironment);
        const isInProgress = [
            ReleaseEnvironmentStatusIndicator.InProgress,
            ReleaseEnvironmentStatusIndicator.PreEvaluatingGates,
            ReleaseEnvironmentStatusIndicator.PostEvaluatingGates].some(s => s === statusIndicator);

        return <ActiveReleaseEnvironmentNodeContent
            {...this.props}
            nodeHeight={environmentNodeHeight}
            nodeWidth={this.props.nodeWidth}
            className={css(this.props.className, "active-rel-env-node")}
            contentClass={css((!!this.props.isLastDeployed || isInProgress) && "highlight")}
            statusIndicator={statusIndicator}
            statusText={statusText}
            computedStatus={computedStatus}
        />;

    }
}

export class ActiveReleaseEnvironmentNodeContent extends VssComponent<IActiveReleaseEnvironmentNodeProps, {}> {

    public render(): JSX.Element {

        const describedByDivId: string = "node-description" + this.context.pageContext.getUniqueNumber();

        return (
            <Tooltip renderContent={this._renderTooltipContent}>
                <div
                    data-is-focusable={true}
                    data-is-grid-focusable={true}
                    onClick={this._onEnvironmentClick}
                    onKeyDown={this._onEnvironmentNodeKeyDown}
                    aria-label={this.props.environment.name}
                    aria-describedby={describedByDivId}
                    style={{ marginRight: this.props.nodeRightMargin }}>
                    <div className="hidden" id={describedByDivId}>{this._getTooltipDescription()}</div>
                    {
                        this._getNodeContent()
                    }
                </div>
            </Tooltip>

        );
    }

    protected get _hasPendingApproval(): boolean {
        return this.props.pendingApprovals.length > 0;
    }

    protected get _hasPendingManualIntervention(): boolean {
        return this.props.computedStatus === ComputedDeploymentStatus.ManualInterventionPending;
    }

    private get _approvalType(): ApprovalType {
        return this._hasPendingApproval ? this.props.pendingApprovals[0].approvalType : ApprovalType.Undefined;
    }

    protected _getNodeContent(): JSX.Element {
        const style = {
            height: this.props.nodeHeight,
            width: this.props.nodeWidth
        };

        // All the browsers excpet chrome are displaying atleast one character and the ellipsis in overflow with min width of environment.
        // To avoid this behaviour and to show ellipsis in other browsers without text we have to add this code as we can't increase min width of environment due to design concerns.
        const is_chrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const showEllipsis = (!is_chrome && (this.props.nodeWidth < 45));

        const containerClassName: string = css("env-summary-node-new-plat", this.props.preventClickOnNode ? "" : "cursor-pointer", "env-" + this.props.environment.id, this.props.className);

        let environmentStatus = this._getStatus();
        let cssBorderClass: string = `${environmentStatus}-env-node`;
        const statusProps: IStatusProps = getIconStatusProps(this.props.statusIndicator as ReleaseEnvironmentStatusIndicator);
        let approvalClass: string = this._hasPendingApproval ? `${this._getApprovalClass()}-env-node` : "";

        return (

            <div className={containerClassName}>

                <div className={css("node-content flex flex-center ", "border", cssBorderClass, approvalClass, this.props.contentClass)}
                    style={style}>

                    {<Status {...statusProps} size={StatusSize.s} className="env-node-icon flex-noshrink" />}

                    <div
                        className="node-text overflow-ellipsis"
                        tabIndex={0}
                        role="button"
                    >
                        {showEllipsis ? "..." : this.props.environment.name}
                    </div>
                </div >
            </div >
        );
    }

    private _getStatus(): string {
        switch (this.props.statusIndicator) {
            case ReleaseEnvironmentStatusIndicator.Canceled:
            case ReleaseEnvironmentStatusIndicator.Canceling:
            case ReleaseEnvironmentStatusIndicator.Failed:
            case ReleaseEnvironmentStatusIndicator.Rejected:
                return ReleaseEnvironmentStatusIndicator.Failed;

            case ReleaseEnvironmentStatusIndicator.InProgress:
            case ReleaseEnvironmentStatusIndicator.PostEvaluatingGates:
                return ReleaseEnvironmentStatusIndicator.InProgress;

            default: return this.props.statusIndicator;
        }
    }

    private _getApprovalClass(): string {
        let approvalClass = this._hasPendingApproval ? (this._approvalType === ApprovalType.PreDeploy ? "pre-approval" : "post-approval") : "";
        return approvalClass;
    }

    protected _renderTooltipContent = (): JSX.Element => {
        let environmentDescription: string = this._getEnvironmentDescription();

        return (
            <div className={"env-summary-node-tooltip-new-plat"}>
                <div >
                    {this.props.environment.name}
                </div>
                <div >
                    {environmentDescription}
                </div>
                {
                    this.props.showArtifactConditionsNotMetMessage && (
                        <div className="tooltip-env-artifact-conditions-not-met-text">{Resources.ArtifactConditionsNotMetText}</div>
                    )
                }
            </div>
        );
    }

    protected _getEnvironmentDescription(): string {
        if (this._hasPendingApproval) {
            let approvals = [];
            for (const approval of this.props.pendingApprovals) {
                const approverText: string = this._isApprovalPendingOnMe(approval) ? Resources.YouText : approval.approver.displayName;
                approvals.push(approverText);
            }
            let approversString = approvals.join(", ");
            return localeFormat(Resources.ApprovalPendingOnFormat, approversString);
        }

        if (this._hasPendingManualIntervention) {
            let pendingManualIntervention: ManualIntervention | null = getFirstMIWithStatus(this.props.environment, ManualInterventionStatus.Pending);
            const createdOnDate: string = pendingManualIntervention && pendingManualIntervention.createdOn ? ago(pendingManualIntervention.createdOn) : "";
            return localeFormat(Resources.WaitingOnManualInterventionCreatedOnText, createdOnDate);
        }

        return this.props.statusText;
    }


    private _getTooltipDescription(): string {
        let tooltipDescription: string = "";
        let environmentDescription = this._getEnvironmentDescription();

        tooltipDescription = format("{0} {1}", this.props.environment.name, environmentDescription);

        return tooltipDescription;
    }

    private _onEnvironmentClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (this.props.preventClickOnNode) {
            return;
        }

        if (this.props.onReleaseFound) {
            this.props.onReleaseFound();
        }

        if (this._hasPendingApproval && this.props.onNodeWithApprovalClicked) {
            this.props.onNodeWithApprovalClicked();
            return;
        }

        if (this._hasPendingManualIntervention && this.props.onNodeWithMIClicked) {
            this.props.onNodeWithMIClicked();
            return;
        }

        const url = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
            "_a": "release-environment-logs",
            "releaseId": this.props.environment.release.id.toString(10),
            "environmentId": this.props.environment.id.toString(10),
            "project": this.props.projectId
        });

        handleEventAndNavigate(this.context.pageContext, event, url);
    }

    private _onEnvironmentNodeKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === "" || event.key === "Spacebar") {
            if (this._onEnvironmentClick) {
                this._onEnvironmentClick(event);
            }
            event.preventDefault();
            event.stopPropagation();
        }
    }

    private _isApprovalPendingOnMe(approval: ReleaseApproval): boolean {
        const pageData = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (!pageData) {
            return false;
        }

        return approval.approver.id.toLowerCase() === pageData.user.id.toLowerCase();
    }

}